import type { ChzFilters, ChzInitError, ChzKpiFilterKey, ChzLogRow, ChzRowStatus, ChzSortConfig } from './types'
import { compareNaturalText } from '@/shared/formatters'

/** Синхронизация устарела строго позже чем через 3 суток */
export const CHZ_STALE_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Парсит дату из ответа API: русский формат "ДД.ММ.ГГГГ, ЧЧ:мм:сс",
 * ISO-строки и прочее.
 */
export function parseChzDate(value: unknown): Date | null {
  if (value == null || value === '') return null
  const s = String(value).trim()
  const ru = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})[,\s]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/)
  if (ru) {
    const dd = Number(ru[1])
    const mm = Number(ru[2]) - 1
    const yyyy = Number(ru[3])
    const hh = Number(ru[4])
    const mi = Number(ru[5])
    const ss = ru[6] != null ? Number(ru[6]) : 0
    const d = new Date(yyyy, mm, dd, hh, mi, ss)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const t = new Date(s)
  return Number.isNaN(t.getTime()) ? null : t
}

export function getRowStatus(log: ChzLogRow): ChzRowStatus {
  return log.status === 'ready' ? 'ready' : 'problem'
}

export function hasValidSync(log: ChzLogRow): boolean {
  return parseChzDate(log.last_sync) != null
}

export function isStaleSync(log: ChzLogRow): boolean {
  const d = parseChzDate(log.last_sync)
  if (!d) return false
  return Date.now() - d.getTime() > CHZ_STALE_MS
}

export function needsAttention(log: ChzLogRow): boolean {
  return getRowStatus(log) === 'problem' || !hasValidSync(log) || isStaleSync(log)
}

export function formatChzDate(dateStr: unknown): string {
  if (!dateStr || dateStr === 'N/A') return '—'
  const date = parseChzDate(dateStr)
  if (!date) return String(dateStr)
  return date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
}

export function applyChzFilters(data: ChzLogRow[], filters: ChzFilters): ChzLogRow[] {
  const s = (filters.search || '').toLowerCase().trim()
  const statusF = filters.status || ''
  const syncPreset = filters.syncPreset || ''

  return data.filter((log) => {
    const name = (log.store_name || '').toLowerCase()
    const ip = (log.store_ip || '').toLowerCase()
    const idStr = String(log.store_id || '')
    const matchesSearch = !s || name.includes(s) || ip.includes(s) || idStr.includes(s)

    const rowStatus = getRowStatus(log)
    const matchesStatus = !statusF || rowStatus === statusF

    let matchesSync = true
    if (syncPreset === 'stale3d') matchesSync = isStaleSync(log)
    else if (syncPreset === 'no-sync') matchesSync = !hasValidSync(log)
    else if (syncPreset === 'attention') matchesSync = needsAttention(log)

    return matchesSearch && matchesStatus && matchesSync
  })
}

export function sortChzData(data: ChzLogRow[], sort: ChzSortConfig): ChzLogRow[] {
  const { key, direction } = sort
  return [...data].sort((a, b) => {
    if (key === 'store_name') {
      const byName = compareNaturalText(a.store_name, b.store_name)
      const result = byName || a.store_id - b.store_id
      return direction === 'asc' ? result : -result
    }

    let valueA: string | number | Date
    let valueB: string | number | Date

    if (key === 'status') {
      const order: Record<ChzRowStatus, number> = { problem: 0, ready: 1 }
      valueA = order[getRowStatus(a)] ?? 2
      valueB = order[getRowStatus(b)] ?? 2
    } else if (key === 'last_sync' || key === 'created_at') {
      valueA = parseChzDate(a[key]) || new Date(0)
      valueB = parseChzDate(b[key]) || new Date(0)
    } else {
      valueA = (a[key as keyof ChzLogRow] as string) || ''
      valueB = (b[key as keyof ChzLogRow] as string) || ''
    }

    if (valueA < valueB) return direction === 'asc' ? -1 : 1
    if (valueA > valueB) return direction === 'asc' ? 1 : -1
    return 0
  })
}

export function computeChzKpi(data: ChzLogRow[]) {
  return {
    total: data.length,
    ready: data.filter((x) => getRowStatus(x) === 'ready').length,
    problem: data.filter((x) => getRowStatus(x) === 'problem').length,
    stale: data.filter(isStaleSync).length,
    noSync: data.filter((x) => !hasValidSync(x)).length,
    attention: data.filter(needsAttention).length,
  }
}

export function filtersFromKpi(key: ChzKpiFilterKey): Partial<ChzFilters> {
  switch (key) {
    case 'all':
      return { search: '', status: '', syncPreset: '' }
    case 'ready':
      return { status: 'ready', syncPreset: '' }
    case 'problem':
      return { status: 'problem', syncPreset: '' }
    case 'stale3d':
      return { status: '', syncPreset: 'stale3d' }
    case 'no-sync':
      return { status: '', syncPreset: 'no-sync' }
    case 'attention':
      return { status: '', syncPreset: 'attention' }
    default:
      return {}
  }
}

export function formatChzInitError(data: ChzInitError | null | undefined): string {
  if (!data || typeof data !== 'object') return 'Ошибка инициализации'

  const reason = data.chzResponse?.reason
  const errorCode = data.chzResponse?.errorCode
  const chzStatus = data.chzStatus

  if (reason === 'unacceptable system status: sync_error') {
    return 'Инициализация недоступна: ошибка синхронизации ЧЗ (sync_error).'
  }

  const parts = [
    data.error || 'Ошибка инициализации',
    reason ? `Причина: ${reason}` : null,
    typeof errorCode === 'number' ? `Код CHZ: ${errorCode}` : null,
    typeof chzStatus === 'number' ? `HTTP CHZ: ${chzStatus}` : null,
  ].filter(Boolean)

  return parts.join(' | ')
}
